// Hex renderer with plain Canvas2D

import type { Hex, Point, HexLayout, Room, Corridor } from "../types.ts";
import { hexToPixel, hexCorners, hexToKey, keyToHex, hexNeighbors } from "../hex/coordinates.ts";
import { findBestLabelPosition } from "../hex/algorithms.ts";
import { PALETTE, TYPOGRAPHY } from "../ui/theme.ts";

export interface HexRenderOptions {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
}

export interface GridRenderOptions {
  stroke?: string;
  strokeWidth?: number;
}

/**
 * Renders hexes and dungeon elements with clean canvas rendering
 */
export class HexRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not get 2d context");
    this.ctx = ctx;
  }

  /**
   * Generate a deterministic seed from hex position
   */
  private hexSeed(hex: Hex): number {
    return Math.abs(((hex.q * 73856093) ^ (hex.r * 19349663)) | 0);
  }

  /**
   * Generate a deterministic seed from a room ID
   */
  private roomSeed(roomId: string): number {
    let hash = 0;
    for (let i = 0; i < roomId.length; i++) {
      const char = roomId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  }

  /**
   * Extract the boundary edges of a hex region as an ordered polygon.
   */
  private extractRoomBoundary(hexes: Set<string>, layout: HexLayout): Point[] {
    if (hexes.size === 0) return [];

    const boundaryEdges: Array<{ start: Point; end: Point; midpoint: Point }> = [];

    for (const key of hexes) {
      const hex = keyToHex(key);
      const corners = hexCorners(hex, layout);
      const neighbors = hexNeighbors(hex);

      for (let i = 0; i < 6; i++) {
        const neighborKey = hexToKey(neighbors[i]);
        if (!hexes.has(neighborKey)) {
          const start = corners[i];
          const end = corners[(i + 1) % 6];
          const midpoint = {
            x: (start.x + end.x) / 2,
            y: (start.y + end.y) / 2,
          };
          boundaryEdges.push({ start, end, midpoint });
        }
      }
    }

    if (boundaryEdges.length === 0) return [];

    const orderedPoints: Point[] = [];
    const used = new Set<number>();

    let currentEdge = boundaryEdges[0];
    used.add(0);
    orderedPoints.push(currentEdge.start);
    orderedPoints.push(currentEdge.end);

    const tolerance = 0.01;
    while (used.size < boundaryEdges.length) {
      const lastPoint = orderedPoints[orderedPoints.length - 1];
      let foundNext = false;

      for (let i = 0; i < boundaryEdges.length; i++) {
        if (used.has(i)) continue;

        const edge = boundaryEdges[i];
        const distToStart = Math.hypot(edge.start.x - lastPoint.x, edge.start.y - lastPoint.y);
        const distToEnd = Math.hypot(edge.end.x - lastPoint.x, edge.end.y - lastPoint.y);

        if (distToStart < tolerance) {
          orderedPoints.push(edge.end);
          used.add(i);
          foundNext = true;
          break;
        } else if (distToEnd < tolerance) {
          orderedPoints.push(edge.start);
          used.add(i);
          foundNext = true;
          break;
        }
      }

      if (!foundNext) break;
    }

    return orderedPoints;
  }

  /**
   * Render the hex grid background
   */
  renderGrid(
    ctx: CanvasRenderingContext2D,
    layout: HexLayout,
    bounds: { minQ: number; maxQ: number; minR: number; maxR: number },
    options: GridRenderOptions = {}
  ): void {
    const { stroke = "#3a3a3a", strokeWidth = 0.5 } = options;

    ctx.save();
    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeWidth;
    ctx.beginPath();

    for (let q = bounds.minQ; q <= bounds.maxQ; q++) {
      for (let r = bounds.minR; r <= bounds.maxR; r++) {
        const hex = { q, r };
        const corners = hexCorners(hex, layout);

        ctx.moveTo(corners[0].x, corners[0].y);
        for (let i = 1; i < corners.length; i++) {
          ctx.lineTo(corners[i].x, corners[i].y);
        }
        ctx.lineTo(corners[0].x, corners[0].y);
      }
    }

    ctx.stroke();
    ctx.restore();
  }

  /**
   * Render a single hex with the given options.
   */
  renderHex(hex: Hex, layout: HexLayout, options: HexRenderOptions = {}): void {
    const {
      fill = "#f4ece0",
      stroke = "#5c4a3d",
      strokeWidth = 1.8,
    } = options;

    const corners = hexCorners(hex, layout);

    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) {
      this.ctx.lineTo(corners[i].x, corners[i].y);
    }
    this.ctx.closePath();

    this.ctx.fillStyle = fill;
    this.ctx.fill();
    this.ctx.strokeStyle = stroke;
    this.ctx.lineWidth = strokeWidth;
    this.ctx.stroke();
    this.ctx.restore();
  }

  /**
   * Render a highlight on a hex (for hover/selection)
   */
  renderHexHighlight(
    ctx: CanvasRenderingContext2D,
    hex: Hex,
    layout: HexLayout,
    color: string = "rgba(255, 200, 100, 0.3)"
  ): void {
    const corners = hexCorners(hex, layout);

    ctx.beginPath();
    ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) {
      ctx.lineTo(corners[i].x, corners[i].y);
    }
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
  }

  /**
   * Render a room with clean styling.
   * @param room - The room to render
   * @param layout - Hex layout for positioning
   * @param fillColorOverride - Optional fill color to use instead of room.style.fill
   */
  renderRoom(room: Room, layout: HexLayout, fillColorOverride?: string): void {
    const ctx = this.ctx;
    const fillColor = fillColorOverride ?? room.style.fill;

    for (const key of room.hexes) {
      const hex = keyToHex(key);
      const corners = hexCorners(hex, layout);

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < corners.length; i++) {
        ctx.lineTo(corners[i].x, corners[i].y);
      }
      ctx.closePath();

      ctx.fillStyle = fillColor;
      ctx.fill();
      ctx.strokeStyle = room.style.stroke;
      ctx.lineWidth = room.userDrawn ? 2 : 1.5;
      ctx.stroke();
      ctx.restore();
    }

    // Labels are rendered separately via renderAllRoomLabels for proper overlap handling
  }

  /**
   * Render all room labels with smart placement and overlap avoidance.
   * Should be called after all rooms are rendered.
   */
  renderAllRoomLabels(rooms: Room[], layout: HexLayout): void {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return;

    // Track occupied label positions for overlap avoidance
    const occupiedPositions: Array<{ x: number; y: number; width: number; height: number }> = [];

    // Sort rooms by size (larger rooms get priority for label placement)
    const sortedRooms = [...rooms].sort((a, b) => b.hexes.size - a.hexes.size);

    for (const room of sortedRooms) {
      if (room.hexes.size < 3) continue;

      const hasLabel = room.label && room.label.trim() !== "";

      // Find the best position for this label
      const position = findBestLabelPosition(room.hexes, layout, occupiedPositions);

      if (hasLabel) {
        const labelBounds = this.renderRoomLabel(room, layout, position);
        if (labelBounds) {
          occupiedPositions.push(labelBounds);
        }
      } else {
        const placeholderBounds = this.renderUnlabeledPlaceholder(room, layout, position);
        if (placeholderBounds) {
          occupiedPositions.push(placeholderBounds);
        }
      }
    }
  }

  /**
   * Render a room label at the specified position with improved styling.
   * Returns the bounding box of the rendered label.
   */
  private renderRoomLabel(
    room: Room,
    layout: HexLayout,
    position: Point
  ): { x: number; y: number; width: number; height: number } | null {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return null;

    // Scale font size based on room size and layout
    const baseFontSize = Math.max(13, layout.size * 0.5);
    const fontSize = Math.min(baseFontSize * (1 + room.hexes.size * 0.02), baseFontSize * 1.4);

    // Truncate long labels based on room size
    const maxLen = Math.max(10, Math.floor(room.hexes.size * 1.2));
    const label = room.label.length > maxLen
      ? room.label.substring(0, maxLen - 1) + "…"
      : room.label;

    ctx.save();

    // Set up font for measurement
    ctx.font = `600 ${fontSize}px ${TYPOGRAPHY.handwritten}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Measure text for background
    const metrics = ctx.measureText(label);
    const textWidth = metrics.width;
    const textHeight = fontSize;
    const paddingX = 6;
    const paddingY = 3;
    const bgWidth = textWidth + paddingX * 2;
    const bgHeight = textHeight + paddingY * 2;

    ctx.translate(position.x, position.y);

    // Draw subtle background pill with slight transparency
    ctx.fillStyle = 'rgba(255, 252, 245, 0.92)';
    ctx.beginPath();
    ctx.roundRect(-bgWidth / 2, -bgHeight / 2, bgWidth, bgHeight, bgHeight / 2);
    ctx.fill();

    // Draw subtle border
    ctx.strokeStyle = 'rgba(92, 74, 61, 0.15)';
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Draw text with subtle shadow for depth
    ctx.shadowColor = 'rgba(0, 0, 0, 0.08)';
    ctx.shadowBlur = 1;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1;

    // Main text
    ctx.fillStyle = PALETTE.ink.brown;
    ctx.fillText(label, 0, 0);

    ctx.restore();

    return {
      x: position.x,
      y: position.y,
      width: bgWidth,
      height: bgHeight,
    };
  }

  /**
   * Render a faint placeholder for unlabeled rooms.
   * Returns the bounding box.
   */
  private renderUnlabeledPlaceholder(
    room: Room,
    layout: HexLayout,
    position: Point
  ): { x: number; y: number; width: number; height: number } | null {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) return null;

    const fontSize = Math.max(14, layout.size * 0.6);

    ctx.save();
    ctx.translate(position.x, position.y);
    ctx.font = `${fontSize}px ${TYPOGRAPHY.handwritten}`;
    ctx.fillStyle = PALETTE.ink.faded + '40';
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("?", 0, 0);
    ctx.restore();

    return {
      x: position.x,
      y: position.y,
      width: fontSize,
      height: fontSize,
    };
  }

  /**
   * Render a stroke preview while drawing
   */
  renderStrokePreview(
    ctx: CanvasRenderingContext2D,
    points: Point[],
    color: string = "#666"
  ): void {
    if (points.length < 2) return;

    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    if (points.length < 4) {
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.stroke();
      ctx.restore();
      return;
    }

    // Draw with variable width based on velocity
    const baseWidth = 2.5;
    const minWidth = 1.0;
    const maxWidth = 4.0;

    for (let i = 1; i < points.length - 1; i++) {
      const p0 = points[i - 1];
      const p1 = points[i];
      const p2 = points[i + 1];

      const velocity = Math.hypot(p2.x - p0.x, p2.y - p0.y);
      const velocityFactor = Math.min(velocity / 30, 1);
      const width = Math.max(minWidth, Math.min(maxWidth, baseWidth - velocityFactor * 1.5));

      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(p0.x, p0.y);

      const midX = (p1.x + p2.x) / 2;
      const midY = (p1.y + p2.y) / 2;
      ctx.quadraticCurveTo(p1.x, p1.y, midX, midY);
      ctx.stroke();
    }

    if (points.length >= 2) {
      const last = points[points.length - 1];
      const prev = points[points.length - 2];
      ctx.lineWidth = baseWidth;
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(last.x, last.y);
      ctx.stroke();
    }

    ctx.restore();
  }

  /**
   * Render a set of hexes as a preview (lighter opacity)
   */
  renderHexesPreview(
    ctx: CanvasRenderingContext2D,
    hexes: Set<string>,
    layout: HexLayout,
    color: string = "rgba(100, 150, 255, 0.3)"
  ): void {
    for (const key of hexes) {
      const [q, r] = key.split(",").map(Number);
      const hex = { q, r };
      this.renderHexHighlight(ctx, hex, layout, color);
    }
  }

  /**
   * Render a corridor (path of hexes connecting rooms)
   */
  renderCorridor(corridor: Corridor, layout: HexLayout): void {
    const ctx = this.ctx;

    // First pass: draw hex fills with darker stone color
    for (const key of corridor.hexes) {
      const hex = keyToHex(key);
      const corners = hexCorners(hex, layout);

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < corners.length; i++) {
        ctx.lineTo(corners[i].x, corners[i].y);
      }
      ctx.closePath();

      // Darker stone/path color
      ctx.fillStyle = "#a89880";
      ctx.fill();
      ctx.strokeStyle = "#6b5d4d";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.restore();
    }

    // Second pass: draw center path line connecting hex centers
    if (corridor.hexes.length >= 2) {
      ctx.save();
      ctx.strokeStyle = "#5a4a3a";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.setLineDash([]);

      ctx.beginPath();
      for (let i = 0; i < corridor.hexes.length; i++) {
        const hex = keyToHex(corridor.hexes[i]);
        const center = hexToPixel(hex, layout);
        if (i === 0) {
          ctx.moveTo(center.x, center.y);
        } else {
          ctx.lineTo(center.x, center.y);
        }
      }
      ctx.stroke();

      // Draw lighter inner line for depth
      ctx.strokeStyle = "#c4b8a8";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let i = 0; i < corridor.hexes.length; i++) {
        const hex = keyToHex(corridor.hexes[i]);
        const center = hexToPixel(hex, layout);
        if (i === 0) {
          ctx.moveTo(center.x, center.y);
        } else {
          ctx.lineTo(center.x, center.y);
        }
      }
      ctx.stroke();

      ctx.restore();
    }
  }

  /**
   * Render selection highlight around a room or corridor
   */
  renderSelectionHighlight(
    ctx: CanvasRenderingContext2D,
    hexes: Set<string> | string[],
    layout: HexLayout,
    color: string = "rgba(100, 150, 255, 0.4)"
  ): void {
    const hexSet = Array.isArray(hexes) ? new Set(hexes) : hexes;
    for (const key of hexSet) {
      const hex = keyToHex(key);
      this.renderHexHighlight(ctx, hex, layout, color);
    }
  }

  /**
   * Render a dashed outline around selected hexes
   */
  renderSelectionOutline(
    ctx: CanvasRenderingContext2D,
    hexes: Set<string> | string[],
    layout: HexLayout,
    color: string = "#4a9eff"
  ): void {
    const hexSet = Array.isArray(hexes) ? new Set(hexes) : hexes;

    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.setLineDash([5, 3]);

    for (const key of hexSet) {
      const hex = keyToHex(key);
      const corners = hexCorners(hex, layout);

      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < corners.length; i++) {
        ctx.lineTo(corners[i].x, corners[i].y);
      }
      ctx.closePath();
      ctx.stroke();
    }

    ctx.setLineDash([]);
  }

  /**
   * Render a corridor preview while drawing
   */
  renderCorridorPreview(
    ctx: CanvasRenderingContext2D,
    hexes: string[],
    layout: HexLayout,
    color: string = "rgba(200, 180, 140, 0.5)"
  ): void {
    for (const key of hexes) {
      const hex = keyToHex(key);
      this.renderHexHighlight(ctx, hex, layout, color);
    }
  }

  /**
   * Render a pulsing glow around a room to indicate it has unmet desires.
   */
  renderDesireGlow(
    ctx: CanvasRenderingContext2D,
    room: Room,
    layout: HexLayout,
    desireType: 'needs-label' | 'wants-connection',
    animTime: number,
    strength: number = 0.6
  ): void {
    const pulse = Math.sin(animTime / 1000 * Math.PI) * 0.5 + 0.5;

    const baseAlpha = desireType === 'needs-label' ? 0.15 : 0.1;
    const alpha = (baseAlpha + pulse * 0.1) * strength;

    const glowColor = desireType === 'needs-label'
      ? `rgba(212, 165, 116, ${alpha})`
      : `rgba(150, 130, 100, ${alpha})`;

    ctx.save();

    for (const key of room.hexes) {
      const hex = keyToHex(key);
      const corners = hexCorners(hex, layout);

      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < corners.length; i++) {
        ctx.lineTo(corners[i].x, corners[i].y);
      }
      ctx.closePath();
      ctx.fillStyle = glowColor;
      ctx.fill();
    }

    if (desireType === 'needs-label') {
      ctx.shadowColor = `rgba(212, 165, 116, ${alpha * 0.5})`;
      ctx.shadowBlur = 8 + pulse * 4;

      const boundary = this.extractRoomBoundary(room.hexes, layout);
      if (boundary.length > 2) {
        ctx.beginPath();
        ctx.moveTo(boundary[0].x, boundary[0].y);
        for (let i = 1; i < boundary.length; i++) {
          ctx.lineTo(boundary[i].x, boundary[i].y);
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(212, 165, 116, ${alpha})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  /**
   * Render potential connection lines between rooms that aren't connected.
   */
  renderPotentialConnections(
    ctx: CanvasRenderingContext2D,
    rooms: Room[],
    corridors: Map<string, Corridor>,
    layout: HexLayout,
    animTime: number
  ): void {
    if (rooms.length < 2) return;

    const connected = new Set<string>();
    for (const corridor of corridors.values()) {
      if (corridor.connects.length >= 2) {
        const key1 = `${corridor.connects[0]}|${corridor.connects[1]}`;
        const key2 = `${corridor.connects[1]}|${corridor.connects[0]}`;
        connected.add(key1);
        connected.add(key2);
      }
    }

    ctx.save();

    const pulse = Math.sin(animTime / 800) * 0.5 + 0.5;

    for (let i = 0; i < rooms.length; i++) {
      for (let j = i + 1; j < rooms.length; j++) {
        const roomA = rooms[i];
        const roomB = rooms[j];

        const key = `${roomA.id}|${roomB.id}`;
        if (connected.has(key)) continue;

        const centerA = this.getRoomCenter(roomA, layout);
        const centerB = this.getRoomCenter(roomB, layout);

        const distance = Math.hypot(centerB.x - centerA.x, centerB.y - centerA.y);
        if (distance > layout.size * 8) continue;

        ctx.strokeStyle = `rgba(150, 130, 100, ${0.15 + pulse * 0.1})`;
        ctx.setLineDash([4, 6]);
        ctx.lineWidth = 1.5;

        ctx.beginPath();
        ctx.moveTo(centerA.x, centerA.y);
        ctx.lineTo(centerB.x, centerB.y);
        ctx.stroke();
      }
    }

    ctx.setLineDash([]);
    ctx.restore();
  }

  /**
   * Render a directional chevron indicating a room wants to connect outward.
   */
  renderConnectionChevron(
    ctx: CanvasRenderingContext2D,
    room: Room,
    layout: HexLayout,
    connectedRoomCenter: Point | null,
    animTime: number,
    strength: number = 0.4
  ): void {
    const center = this.getRoomCenter(room, layout);

    let dirX: number, dirY: number;
    if (connectedRoomCenter) {
      dirX = center.x - connectedRoomCenter.x;
      dirY = center.y - connectedRoomCenter.y;
    } else {
      dirX = center.x || 1;
      dirY = center.y || 0;
    }

    const len = Math.hypot(dirX, dirY);
    if (len < 0.001) return;
    dirX /= len;
    dirY /= len;

    const pulse = Math.sin(animTime / 600) * 0.5 + 0.5;
    const alpha = (0.3 + pulse * 0.2) * strength;

    const offset = layout.size * 1.5;
    const chevronX = center.x + dirX * offset;
    const chevronY = center.y + dirY * offset;

    const size = layout.size * 0.4;
    const angle = Math.atan2(dirY, dirX);

    ctx.save();
    ctx.translate(chevronX, chevronY);
    ctx.rotate(angle);

    ctx.strokeStyle = `rgba(150, 130, 100, ${alpha})`;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(-size * 0.6, -size * 0.5);
    ctx.lineTo(size * 0.4, 0);
    ctx.lineTo(-size * 0.6, size * 0.5);
    ctx.stroke();

    ctx.restore();
  }

  /**
   * Get the center point of a room
   */
  private getRoomCenter(room: Room, layout: HexLayout): Point {
    let sumX = 0, sumY = 0;
    for (const key of room.hexes) {
      const hex = keyToHex(key);
      const center = hexToPixel(hex, layout);
      sumX += center.x;
      sumY += center.y;
    }
    return {
      x: sumX / room.hexes.size,
      y: sumY / room.hexes.size,
    };
  }

  /**
   * Calculate visible hex bounds from canvas dimensions and layout
   */
  static calculateVisibleBounds(
    width: number,
    height: number,
    layout: HexLayout
  ): { minQ: number; maxQ: number; minR: number; maxR: number } {
    const padding = 2;
    const hexWidth = Math.sqrt(3) * layout.size;
    const hexHeight = 2 * layout.size;

    const minQ = Math.floor(-layout.origin.x / hexWidth) - padding;
    const maxQ = Math.ceil((width - layout.origin.x) / hexWidth) + padding;
    const minR = Math.floor(-layout.origin.y / (hexHeight * 0.75)) - padding;
    const maxR = Math.ceil((height - layout.origin.y) / (hexHeight * 0.75)) + padding;

    return { minQ, maxQ, minR, maxR };
  }

  /**
   * Render subtle constraint boundaries around existing rooms while drawing.
   */
  renderConstraintBoundaries(
    ctx: CanvasRenderingContext2D,
    rooms: Room[],
    layout: HexLayout,
    glowColor: string = "rgba(255, 255, 255, 0.15)"
  ): void {
    ctx.save();

    for (const room of rooms) {
      for (const key of room.hexes) {
        const hex = keyToHex(key);
        const corners = hexCorners(hex, layout);

        ctx.beginPath();
        ctx.moveTo(corners[0].x, corners[0].y);
        for (let i = 1; i < corners.length; i++) {
          ctx.lineTo(corners[i].x, corners[i].y);
        }
        ctx.closePath();
        ctx.fillStyle = glowColor;
        ctx.fill();
      }
    }

    ctx.restore();
  }

  /**
   * Render an "action hint" showing what operation will occur
   */
  renderActionHint(
    ctx: CanvasRenderingContext2D,
    position: Point,
    action: "extend" | "merge" | "new" | "corridor",
    layout: HexLayout
  ): void {
    const labels: Record<string, { text: string; color: string }> = {
      extend: { text: "EXTEND", color: "rgba(100, 200, 180, 0.9)" },
      merge: { text: "MERGE", color: "rgba(100, 150, 200, 0.9)" },
      new: { text: "NEW ROOM", color: "rgba(100, 200, 100, 0.9)" },
      corridor: { text: "CORRIDOR", color: "rgba(200, 170, 100, 0.9)" },
    };

    const label = labels[action];
    if (!label) return;

    ctx.save();
    ctx.font = "bold 10px system-ui, sans-serif";
    ctx.fillStyle = label.color;
    ctx.textAlign = "center";
    ctx.fillText(label.text, position.x, position.y - 30);
    ctx.restore();
  }
}
